import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ListChecks, BookOpen, Inbox, UserPlus, BarChart3, Mail, GraduationCap } from "lucide-react";
import Seo from "@/components/seo/Seo";

interface Counts {
  questions: number;
  subjects: number;
  reports: number;
  suggestions: number;
}

export default function AdminDashboard() {
  const [counts, setCounts] = useState<Counts>({ questions: 0, subjects: 0, reports: 0, suggestions: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [q, s, r, c] = await Promise.all([
        supabase.from("questions").select("id", { count: "exact", head: true }),
        supabase.from("subjects").select("id", { count: "exact", head: true }),
        supabase.from("question_reports").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("category_suggestions").select("id", { count: "exact", head: true }).eq("status", "pending"),
      ]);
      setCounts({
        questions: q.count ?? 0,
        subjects: s.count ?? 0,
        reports: r.count ?? 0,
        suggestions: c.count ?? 0,
      });
      setLoading(false);
    })();
  }, []);

  const kpis = [
    { label: "Questions", value: counts.questions, icon: ListChecks, to: "/admin/questions" },
    { label: "Subjects", value: counts.subjects, icon: BookOpen, to: "/admin/subjects" },
    { label: "Pending reports", value: counts.reports, icon: Inbox, to: "/admin/reports" },
    { label: "Category suggestions", value: counts.suggestions, icon: UserPlus, to: "/admin/category-suggestions" },
  ];

  const shortcuts = [
    { label: "Generate questions", desc: "AI-assisted question creation", to: "/admin/generator", icon: GraduationCap },
    { label: "AI logs", desc: "Inspect generation activity", to: "/admin/logs", icon: BarChart3 },
    { label: "Email templates", desc: "Manage transactional templates", to: "/admin/email-templates", icon: Mail },
    { label: "Email campaigns", desc: "Schedule and send campaigns", to: "/admin/email-campaigns", icon: Mail },
  ];

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <Seo title="Admin Dashboard" description="Admin tools and platform health for Grade Test Pro." canonicalPath="/admin" noindex />
      <header>
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <p className="text-muted-foreground">Platform health and quick actions.</p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Link key={k.label} to={k.to}>
            <Card className="hover:bg-accent/50 transition-colors h-full">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{k.label}</CardTitle>
                <k.icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{loading ? "—" : k.value}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick actions</CardTitle>
          <CardDescription>Jump into common admin tasks.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {shortcuts.map((s) => (
            <Button key={s.label} asChild variant="outline" className="h-auto justify-start py-3">
              <Link to={s.to}>
                <s.icon className="h-5 w-5 mr-3 shrink-0" />
                <span className="flex flex-col items-start text-left">
                  <span className="font-medium">{s.label}</span>
                  <span className="text-xs text-muted-foreground">{s.desc}</span>
                </span>
              </Link>
            </Button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
